import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND, SERVICES } from "../lib/brand";
import { Phone, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  address: "",
  service: "Termite Control",
  property_type: "Home",
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      setForm(initial);
      toast.success("Thanks! We'll be in touch within one business day.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please call 1-800-DEAD BUG.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative bg-[color:var(--dbe-cream)] py-20 sm:py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 grid lg:grid-cols-5 gap-8 lg:gap-10 items-start">
        <div className="lg:col-span-2">
          <p className="text-[color:var(--dbe-red)] text-xs tracking-[0.35em] uppercase font-bold">Free Estimate</p>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-bebas">
            Send the bugs<br /><span className="text-[color:var(--dbe-red)]">packing.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-neutral-700 leading-relaxed">
            Tell us a little about your pest problem and we'll follow up within one business day
            with honest pricing and a plan.
          </p>

          <div className="mt-8 bg-white border border-neutral-200 rounded-2xl p-6 sm:p-7 shadow-[0_20px_50px_-30px_rgba(15,15,25,0.15)]">
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--dbe-red)] font-bold">Prefer to call?</div>
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="quote-phone-link"
              className="mt-2 block font-bebas text-4xl sm:text-5xl leading-none tracking-wider text-[color:var(--dbe-red)] break-words"
            >
              {BRAND.phoneDisplay}
            </a>
            <div className="mt-5 text-sm text-neutral-600">
              <div><i className="fa-regular fa-clock mr-2 text-[color:var(--dbe-red)]" />{BRAND.hours}</div>
              <div className="mt-1"><i className="fa-solid fa-location-dot mr-2 text-[color:var(--dbe-red)]" />{BRAND.address.line1}, {BRAND.address.city}, {BRAND.address.state}</div>
            </div>
          </div>

          <ul className="mt-8 space-y-3 text-neutral-800">
            {["No obligation, ever", "Real humans, zero phone trees", "Service guarantee between visits"].map((i) => (
              <li key={i} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-[color:var(--dbe-red)] flex-shrink-0" />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="bg-white rounded-[24px] sm:rounded-[28px] p-6 sm:p-8 md:p-10 border border-black/5 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.25)]"
          >
            {done ? (
              <div data-testid="quote-success" className="text-center py-10">
                <div className="mx-auto w-16 h-16 rounded-full bg-[color:var(--dbe-red)]/10 text-[color:var(--dbe-red)] flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="mt-5 font-bebas text-3xl">Estimate request received</h3>
                <p className="mt-2 text-neutral-600">A real human from Dead Bug Edwards will reach out shortly.</p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-6 text-[color:var(--dbe-red)] font-bold uppercase tracking-wider text-sm"
                  data-testid="quote-reset-btn"
                >
                  Submit another →
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name" name="name" value={form.name} onChange={onChange} required />
                  <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} required />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                  <Field label="Service Address" name="address" value={form.address} onChange={onChange} />
                  <Select
                    label="Service Needed"
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    options={SERVICES.map((s) => s.title).concat(["Not Sure / Inspection"])}
                  />
                  <Select
                    label="Property Type"
                    name="property_type"
                    value={form.property_type}
                    onChange={onChange}
                    options={["Home", "Marine / Boat", "HOA / Multifamily"]}
                  />
                </div>

                <div className="mt-5">
                  <label className="block text-xs uppercase tracking-[0.2em] text-neutral-600 mb-2">Tell us about the problem</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    data-testid="quote-message-input"
                    className="w-full border border-black/10 rounded-xl bg-[color:var(--dbe-cream)]/40 focus:bg-white px-4 py-3 text-neutral-900 focus:outline-none focus:border-[color:var(--dbe-red)]"
                    placeholder="Where are you seeing pests? How long? Any specifics we should know?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-btn"
                  className="mt-7 w-full inline-flex items-center justify-center gap-3 bg-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red-deep)] text-white px-8 py-4 rounded-full font-bold uppercase tracking-wider text-sm disabled:opacity-70"
                >
                  {loading ? (
                    <><i className="fa-solid fa-spinner fa-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Request My Free Estimate</>
                  )}
                </button>

                <p className="mt-4 text-center text-xs text-neutral-500">
                  By submitting, you agree to be contacted about your request. We'll never share your info.
                  Prefer to call? <a href={`tel:${BRAND.phoneTel}`} className="text-[color:var(--dbe-red)] font-semibold">{BRAND.phoneDisplay}</a>
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-neutral-600 mb-2">
        {label}{required && <span className="text-[color:var(--dbe-red)]"> *</span>}
      </span>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`quote-${name}-input`}
        className="w-full border border-black/10 rounded-xl bg-[color:var(--dbe-cream)]/40 focus:bg-white px-4 py-3 text-neutral-900 focus:outline-none focus:border-[color:var(--dbe-red)]"
      />
    </label>
  );
}

function Select({ label, name, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-neutral-600 mb-2">{label}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        data-testid={`quote-${name}-select`}
        className="w-full border border-black/10 rounded-xl bg-[color:var(--dbe-cream)]/40 focus:bg-white px-4 py-3 text-neutral-900 focus:outline-none focus:border-[color:var(--dbe-red)]"
      >
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
